import React from "react";
import { Code2, Palette, Users, Lightbulb, MessageCircle, Puzzle, Sparkles } from "lucide-react";
import profile from "../assets/2.jpg";

const About = () => {
  const hardSkills = [
    { name: 'HTML', icon: Code2, color: 'text-orange-500' },
    { name: 'CSS', icon: Palette, color: 'text-blue-500' },
    { name: 'JavaScript', icon: Code2, color: 'text-yellow-500' },
    { name: 'React', icon: Code2, color: 'text-cyan-500' },
    { name: 'PHP', icon: Code2, color: 'text-purple-500' },
    { name: 'Laravel', icon: Code2, color: 'text-red-500' },
    { name: 'MySQL', icon: Code2, color: 'text-blue-600' },
    { name: 'Figma', icon: Palette, color: 'text-pink-500' }
  ];

  const softSkills = [
    { name: 'Pemecahan Masalah', icon: Puzzle, color: 'text-[#E6B566]' },
    { name: 'Kerja Tim', icon: Users, color: 'text-[#3A7CA5]' },
    { name: 'Komunikasi', icon: MessageCircle, color: 'text-[#A9D6E5]' },
    { name: 'Inovasi', icon: Lightbulb, color: 'text-[#E6B566]' }
  ];

  return (
    <section id="about" className="py-20 bg-white dark:bg-gray-900 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Profile Info */}
          <div className="space-y-8 animate-slideInLeft">
            <div className="group">
              <h2 className="text-4xl font-bold text-[#3D3D3D] dark:text-white mb-4 group-hover:text-[#3A7CA5] dark:group-hover:text-[#A9D6E5] transition-colors duration-300">
                Tentang Saya
                <Sparkles className="inline-block w-8 h-8 text-[#E6B566] ml-2 animate-pulse" />
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[#3A7CA5] to-[#E6B566] mb-8 group-hover:w-32 transition-all duration-500"></div>
            </div>

            {/* Profile Card */}
            <div className="bg-[#F8F9FA] dark:bg-gray-800 rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 card-hover glow-on-hover group">
              <div className="flex items-center gap-6 mb-6">
                <div className="relative">
                  {/* Profile Image */}
                  <div className="w-20 h-20 rounded-full overflow-hidden group-hover:rotate-12 transition-transform duration-500 flex items-center justify-center bg-gradient-to-br from-[#3A7CA5] to-[#A9D6E5] text-white font-bold relative">
                    <img 
                      src={profile}
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Floating ring */}
                  <div className="absolute inset-0 border-2 border-[#E6B566]/30 rounded-full animate-spin-slow"></div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-[#3D3D3D] dark:text-white mb-1 group-hover:gradient-text transition-all duration-300">
                    Kinandaru Pramudya Prastiwi
                  </h3>
                  <p className="text-[#3A7CA5] dark:text-[#A9D6E5] font-medium mb-1">SMK Negeri 10 Jakarta</p>
                  <p className="text-gray-600 dark:text-gray-400">Rekayasa Perangkat Lunak</p>
                </div>
              </div>

              <p className="text-gray-600 dark:text-gray-300 leading-relaxed relative">
  Saya meyakini bahwa teknologi bukan hanya sekadar barisan kode, 
  melainkan juga tentang bagaimana membantu masyarakat, memecahkan masalah, 
  serta menciptakan peluang baru. Selama dua tahun terakhir, 
  saya mengembangkan keterampilan di bidang frontend dan backend, 
  serta mengintegrasikannya dengan prinsip desain UI/UX yang baik.
                {/* Animated quote mark */}
                <div className="absolute -top-4 -left-4 text-6xl text-[#3A7CA5]/20 dark:text-[#A9D6E5]/20 font-serif opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  "
                </div>
              </p>

              {/* Resume Button */}
              <div className="mt-6">
                <a
  href="/resume.pdf" target="_blank"
  download="Kinandaru-Resume.pdf"
  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#3A7CA5] to-[#2E6B94] text-white rounded-lg shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
>
  Download Resume
</a>

              </div>
            </div>

            <div className="text-center lg:text-left animate-slideInLeft animation-delay-300">
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-2">
                <div className="w-2 h-2 bg-[#3A7CA5] rounded-full animate-pulse"></div>
                <p className="text-lg text-[#3A7CA5] dark:text-[#A9D6E5] font-medium">
                  Focus Areas
                </p>
                <div className="w-2 h-2 bg-[#E6B566] rounded-full animate-pulse animation-delay-500"></div>
              </div>
              <p className="text-gray-600 dark:text-gray-300">
  Pengembangan Web Fullstack • Desain UI/UX • Pemecahan Masalah
</p>
            </div>
          </div>

          {/* Right - Skills */}
          <div className="space-y-10 animate-slideInRight">
            {/* Hard Skills */}
            <div>
              <h3 className="text-2xl font-bold text-[#3D3D3D] dark:text-white mb-6 flex items-center gap-3 group">
                <Code2 className="w-6 h-6 text-[#3A7CA5] dark:text-[#A9D6E5] group-hover:rotate-12 transition-transform duration-300" />
                <span className="group-hover:gradient-text transition-all duration-300">Keterampilan Teknis</span>
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {hardSkills.map((skill, index) => {
                  const IconComponent = skill.icon;
                  return (
                    <div 
                      key={skill.name} 
                      className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-md hover:shadow-2xl transition-all duration-500 transform hover:scale-110 hover:-translate-y-2 border border-gray-100 dark:border-gray-700 card-hover group cursor-pointer animate-fadeInScale"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <IconComponent className={`w-8 h-8 ${skill.color} mb-2 mx-auto group-hover:animate-bounce-slow transition-all duration-300`} />
                      <p className="text-sm font-medium text-center text-[#3D3D3D] dark:text-white group-hover:text-[#3A7CA5] dark:group-hover:text-[#A9D6E5] transition-colors duration-300">
                        {skill.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Soft Skills */}
            <div className="animate-slideInRight animation-delay-500">
              <h3 className="text-2xl font-bold text-[#3D3D3D] dark:text-white mb-6 flex items-center gap-3 group">
                <Users className="w-6 h-6 text-[#E6B566] group-hover:scale-125 transition-transform duration-300" />
                <span className="group-hover:gradient-text transition-all duration-300">Keterampilan Lunak</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {softSkills.map((skill, index) => {
                  const IconComponent = skill.icon;
                  return (
                    <div 
                      key={skill.name} 
                      className="bg-[#F8F9FA] dark:bg-gray-800 p-6 rounded-xl hover:bg-[#A9D6E5]/10 dark:hover:bg-gray-700 transition-all duration-500 border-l-4 border-[#E6B566] hover:border-l-8 transform hover:scale-105 cursor-pointer group animate-slideInUp"
                      style={{ animationDelay: `${index * 150 + 300}ms` }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <IconComponent className={`w-6 h-6 ${skill.color} group-hover:scale-125 group-hover:rotate-12 transition-all duration-300`} />
                          {/* Glowing dot */}
                          <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#E6B566] rounded-full opacity-0 group-hover:opacity-100 animate-pulse transition-opacity duration-300"></div>
                        </div>
                        <p className="font-medium text-[#3D3D3D] dark:text-white group-hover:text-[#3A7CA5] dark:group-hover:text-[#A9D6E5] transition-colors duration-300">
                          {skill.name}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
